/**
 * todoText.js: date labels, ordering and voice note action items for the To Do
 * page (/todos), the Home "My to-dos" card and the voice note to-do buttons.
 *
 * Due dates are calendar days ('YYYY-MM-DD', LA time) compared as strings with
 * the server's `today`. They are read by parts, never with new Date('YYYY-MM-DD'),
 * which is UTC midnight and shows the day before in LA.
 *
 * Copy rule: plain words, short sentences, no em dashes, no double hyphens.
 * Contract: docs/agent-notes/todo-page.md (section 5).
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_MS = 86400000;

// A due date from tomorrow up to this many days ahead counts as "soon".
const SOON_DAYS = 6;

/** 'YYYY-MM-DD' -> whole days since 1970-01-01, or null. Same answer in every timezone. */
function dayNumber(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS;
}

/** Days from `today` to `dueDate` (negative = overdue), or null when either is missing. */
export function daysUntil(dueDate, today) {
  const d = dayNumber(dueDate);
  const t = dayNumber(today);
  return d === null || t === null ? null : d - t;
}

/**
 * "Today", "Tomorrow", "Yesterday", "Mon, Oct 21" (this year) or "Oct 21, 2027"
 * (another year). '' when there is no due date.
 */
export function fmtDue(dueDate, today) {
  const d = dayNumber(dueDate);
  if (d === null) return '';
  const diff = daysUntil(dueDate, today);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  const date = new Date(d * DAY_MS);
  const md = `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
  const sameYear = !!today && String(today).slice(0, 4) === String(dueDate).slice(0, 4);
  return sameYear ? `${WEEKDAYS[date.getUTCDay()]}, ${md}` : `${md}, ${date.getUTCFullYear()}`;
}

/** 'overdue' | 'today' | 'soon' (the next 6 days) | 'later' | 'none' (no due date). */
export function dueTone(dueDate, today) {
  if (dayNumber(dueDate) === null) return 'none';
  const diff = daysUntil(dueDate, today);
  if (diff === null) return 'later';
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  return diff <= SOON_DAYS ? 'soon' : 'later';
}

const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

const byFinished = (a, b) => cmp(b.completedAt || '', a.completedAt || '') || cmp(a.id || '', b.id || '');

/**
 * Team view order. Open to-dos first: due date ascending, no due date last, then
 * oldest made first. Done to-dos after them, most recently finished first.
 * Returns a new array.
 */
export function sortTodos(items) {
  const open = [];
  const done = [];
  for (const t of items || []) (t.done ? done : open).push(t);
  open.sort((a, b) => cmp(a.dueDate || '9999-99-99', b.dueDate || '9999-99-99')
    || cmp(a.createdAt || '', b.createdAt || '')
    || cmp(a.id || '', b.id || ''));
  done.sort(byFinished);
  return [...open, ...done];
}

const hasKey = (t) => typeof t.sortKey === 'number';

/**
 * Mine order, the same as the server's (MINE_ORDER in routes/todos.js). Open
 * to-dos by sortKey, lower first; one with no key (added by the code from before
 * the order existed) above every key; then newest made first, then id. Done
 * to-dos after them, most recently finished first. A done to-do keeps its key,
 * so unticking it puts it straight back where it was. Returns a new array.
 */
export function sortMine(items) {
  const open = [];
  const done = [];
  for (const t of items || []) (t.done ? done : open).push(t);
  open.sort((a, b) => (hasKey(a) === hasKey(b) ? 0 : hasKey(a) ? 1 : -1)
    || (hasKey(a) ? a.sortKey - b.sortKey : 0)
    || cmp(b.createdAt || '', a.createdAt || '')
    || cmp(a.id || '', b.id || ''));
  done.sort(byFinished);
  return [...open, ...done];
}

/**
 * A Mine list after a drag, as the server holds it once PUT /todos/order saves
 * (the same steps as its SQL): the open to-dos named in `ids` swap among the
 * places they hold now, in the order of `ids`; an open one left out keeps its
 * place; then every open one gets its index as its sortKey. Done ones are
 * untouched. Ids that are not open items are skipped; when none is open, nothing
 * changes. Returns a new array in sortMine order.
 */
export function applyOrder(items, ids) {
  const at = new Map();
  (ids || []).forEach((id, i) => {
    if (!at.has(id)) at.set(id, i);
  });
  const list = sortMine(items);
  const open = list.filter((t) => !t.done);
  const places = [];
  open.forEach((t, i) => {
    if (at.has(t.id)) places.push(i);
  });
  if (!places.length) return list;
  const moved = places.map((i) => open[i]).sort((a, b) => at.get(a.id) - at.get(b.id));
  const next = [...open];
  places.forEach((place, k) => {
    next[place] = moved[k];
  });
  return [
    ...next.map((t, i) => (t.sortKey === i ? t : { ...t, sortKey: i })),
    ...list.filter((t) => t.done),
  ];
}

const CK_KEY_RE = /^ck_\d{1,3}$/;

/**
 * A voice note's action items, keyed by where each one came from so a retriage
 * that seeds `checklist` never changes a key:
 *   triage.key_requirements[i]  -> kr_i
 *   checklist items whose text is not a key requirement -> their own ck_ id
 *   triage.suggested_actions[i] -> sa_i (older rows hold strings, newer { label, path })
 * Returns [{ key, text }].
 */
export function voiceNoteActionItems(note) {
  const triage = note?.triage || {};
  const items = [];
  const krTexts = new Set();
  (Array.isArray(triage.key_requirements) ? triage.key_requirements : []).forEach((t, i) => {
    const text = String(t ?? '').trim();
    if (!text) return;
    krTexts.add(text);
    items.push({ key: `kr_${i}`, text });
  });
  const ckKeys = new Set();
  (Array.isArray(note?.checklist) ? note.checklist : []).forEach((c, i) => {
    const text = String(c?.text ?? '').trim();
    if (!text || krTexts.has(text)) return;
    const key = CK_KEY_RE.test(c?.id) ? c.id : `ck_${i}`;
    if (ckKeys.has(key)) return;
    ckKeys.add(key);
    items.push({ key, text });
  });
  (Array.isArray(triage.suggested_actions) ? triage.suggested_actions : []).forEach((a, i) => {
    const action = typeof a === 'string' ? { label: a } : a;
    const text = String(action?.label ?? '').trim();
    if (text) items.push({ key: `sa_${i}`, text });
  });
  return items;
}
